import { get, post } from '@/services/httpMethods'
import type { ApiResponse } from '@/types/api'
import type { Service } from '@/data/services'
import type { BlogPost, BlogTaxonomy } from '@/data/blog'

export interface PublicBlogMeta {
  /** Hierarchical category tree with rolled-up post counts. */
  categories: BlogTaxonomy[]
  /** Hierarchical tag tree with post counts. */
  tagTree: BlogTaxonomy[]
  /** Flat tag names (backward compatible). */
  tags: string[]
}

export interface WebsiteBlogQuery {
  /** Category slugs to filter by (matches descendants too). */
  categories?: string[]
  /** Tag slugs to filter by (matches descendants too). */
  tags?: string[]
  /** Free-text search term. */
  search?: string
}

export interface WebsiteContactInfo {
  id?: string
  phone: string
  phoneAlt: string
  email: string
  address: string
  workingHours: string
  mapEmbedUrl: string
  mapLinkUrl: string
  isActive: boolean
}

export interface ContactFormPayload {
  name: string
  email: string
  phone?: string
  subject?: string
  message: string
}

/** A single public gallery asset. Images store a URL; videos add a YouTube link. */
export interface GalleryAsset {
  id: string
  type: 'image' | 'video'
  title: string
  /** Image URL, or the thumbnail URL for video items. */
  image: string
  /** YouTube URL for video items; empty for images. */
  videoUrl: string
  displayOrder: number
}

export interface GalleryData {
  images: GalleryAsset[]
  videos: GalleryAsset[]
}

/** A single public patient testimonial shown on `/testimonials`. */
export interface Testimonial {
  id: string
  name: string
  role: string
  /** Optional heading shown above the quote. */
  title: string
  quote: string
  /** Author avatar image URL (empty when not provided). */
  image: string
  rating: number
  displayOrder: number
}

/** A single public pricing plan shown in the home page "Plans & Pricing" section. */
export interface Plan {
  id: string
  name: string
  price: string
  description: string
  features: string[]
  /** Highlights the plan with a "Popular" badge. */
  popular: boolean
  displayOrder: number
}

/**
 * Public website content endpoints. The website renders these when available
 * and falls back to the bundled demo content (`@/data/*`) when the API is
 * unreachable or the visitor is offline.
 */
export function fetchWebsiteServices() {
  return get<Service[]>('/website/public/services')
}

export function fetchWebsiteService(slug: string) {
  return get<Service>(`/website/public/services/${slug}`)
}

export function fetchWebsiteBlogs(params: WebsiteBlogQuery = {}) {
  const query = new URLSearchParams()
  if (params.categories?.length) query.set('categories', params.categories.join(','))
  if (params.tags?.length) query.set('tags', params.tags.join(','))
  if (params.search) query.set('search', params.search)
  const qs = query.toString()
  return get<BlogPost[]>(`/website/public/blogs${qs ? `?${qs}` : ''}`)
}

export function fetchWebsiteBlogCategories() {
  return get<BlogTaxonomy[]>('/website/public/blog-categories')
}

export function fetchWebsiteBlogTags() {
  return get<BlogTaxonomy[]>('/website/public/blog-tags')
}

export function fetchWebsiteBlog(slug: string) {
  return get<BlogPost>(`/website/public/blogs/${slug}`)
}

export function fetchWebsiteBlogMeta() {
  return get<PublicBlogMeta>('/website/public/blog-meta')
}

export function fetchWebsiteContact() {
  return get<WebsiteContactInfo>('/website/public/contact')
}

/** Active gallery assets grouped into images and videos. */
export function fetchWebsiteGallery() {
  return get<GalleryData>('/website/public/gallery')
}

/** Active testimonials in display order. */
export function fetchWebsiteTestimonials() {
  return get<Testimonial[]>('/website/public/testimonials')
}

/** Active pricing plans in display order. */
export function fetchWebsitePlans() {
  return get<Plan[]>('/website/public/plans')
}

/** Submits the public contact form (unauthenticated). */
export function submitContactForm(payload: ContactFormPayload) {
  return post<{ sent: boolean }>('/website/public/contact', payload)
}

/**
 * Public hospital settings exposed by `/system/public/settings`, keyed by the
 * raw setting name used in the dashboard (e.g. `hospital_logo`).
 */
export type PublicSettingsMap = Record<string, string>

/**
 * Reads the public hospital settings (logo, name, contact details) that are
 * managed from the dashboard's Settings → General page. Unauthenticated.
 */
export function fetchPublicSettings(): Promise<ApiResponse<PublicSettingsMap>> {
  return get<PublicSettingsMap>('/system/public/settings')
}

/** Public website mode controlled from the dashboard developer console. */
export type FrontendMode = 'live' | 'development'

export interface FrontendModeInfo {
  mode: FrontendMode
}

/**
 * Reads the server-side public website mode. When the mode is `development`
 * the public site renders the "under development" placeholder instead of the
 * real marketing pages.
 */
export function fetchFrontendMode(): Promise<ApiResponse<FrontendModeInfo>> {
  return get<FrontendModeInfo>('/system/public/frontend-mode')
}
